/**
 * BaseModel — 低代码平台所有 ViewModel 的基类
 *
 * ## 架构角色
 * 模型驱动的核心。每个页面（卡片/列表/查询区）和每个表单字段都是一个 ViewModel 实例，
 * 全部继承自 BaseModel。模型持有数据 + 事件 + 监听器，React 组件只负责渲染。
 *
 * ## 数据存储
 * ```
 * _data（闭包，外部不可直接访问）:
 *   ├── value / disabled / visible / readOnly / ...  — 业务状态
 *   ├── cItemName / cControlType / field / ...       — meta 配置
 *   ├── listeners[]     — 绑定的 React 组件引用
 *   ├── events{}        — 扩展脚本注册的回调 { eventName: [{callback, context}] }
 *   ├── firstevents{}   — onFirst 注册的优先回调
 *   └── cache{}         — 临时状态（parent/isLoading），不触发监听器
 * ```
 *
 * ## 事件系统（生命周期/钩子）
 * ```
 * vm.on('beforeValueChange', fn)    // 注册监听
 * vm.execute('beforeValueChange', data)  // 派发，回调返回 false 中断
 * vm.fireEvent('valueChange', data) // 自动加前缀: beforeValueChange → valueChange → afterValueChange
 * ```
 * **注意：** fireEvent 会拼 `'before' + eventName`，所以 `fireEvent('beforeXxx')` 会变成 `beforebeforeXxx`。
 * 精确的事件名用 `execute()` 派发。
 *
 * ## React 集成（监听器模式）
 * ```
 * model.addListener(reactComponent)     // 组件挂载时绑定
 * model.removeListener(reactComponent)  // 组件卸载时解绑
 * // 值变更时 model 自动调 listener.setState({...}) 触发重渲染
 * ```
 *
 * ## 继承链
 * BaseModel → SimpleModel（表单字段）/ SearchModel（查询区）/ GridModel（表格）/ CharacterModel（多语）/ ...
 *
 * ## 使用示例
 * ```js
 * // 创建模型
 * var vm = new ttx.models.SimpleModel({ cItemName: 'name', cControlType: 'input' });
 * // 监听事件
 * vm.on('afterValueChange', function(data) { console.log(data.value); });
 * // 组件绑定
 * vm.addListener(reactComp);
 * ```
 */
ttx.models.register('BaseModel', function (modelType) {
    const baseModel = function (data) {
      const propertyNames = [];
      if (data)
        for (const propertyName in data)
          propertyNames.push(propertyName);
      let _data = ttx.utils.extend({}, { listeners: [], propertyNames: propertyNames, events: {}, firstevents: {}, cache: {} }, data);
  
      // 框架内部使用，获取属性
      this._get_data = function (key) {
        if (!key) return;
        return _data[key];
      };
  
      // 对外暴漏方法，获取属性
      this.getProperty = function (key) {
        return this._get_data(key)
      }
  
      // 框架内部使用，设置属性
      this._set_data = function (key, value, update) {
        if (!key) return;
        if (update && propertyNames.indexOf(key) < 0)
          propertyNames.push(key);
        _data[key] = value;
      };
  
      // 对外暴漏方法，设置属性
      this.setProperty = function (key, value, update) {
        this._set_data(key, value, update)
      }
  
      // 框架内部使用，删除属性
      this._del_data = function (key) {
        if (!key) return;
        delete _data[key];
      };
  
      // 对外暴漏方法，删除属性
      this.delProperty = function (key) {
        this._del_data(key)
      }
  
      // 框架内部使用，清空属性
      this._cls_data = function () {
        _data = null;
      };
  
      // 对外暴漏方法，清空属性
      this.clsProperty = function () {
        this._cls_data()
      }
  
      this._data = function () {
        return _data;
      };
      this.__data = _data;
    };
    baseModel.prototype.modelType = modelType;

    // 设置父 ViewModel（构建父子链）
    baseModel.prototype.setParent = function (parent) {
      this.setCache('parent', parent);
    };

    // 获取父 ViewModel
    baseModel.prototype.getParent = function () {
      return this.getCache('parent');
    };

    // 递归向上查找根 ViewModel（表单页面级 VM）
    baseModel.prototype.getRootParent = function () {
      let viewModel = this.getParent();
      while (viewModel?.getParent())
        viewModel = viewModel.getParent();
      return viewModel
    };
  
    baseModel.prototype.getPageBillModel = function () {
      let viewModel = this.getRootParent?.() || this;
      if (viewModel.get('filterModel')) {
        const filterModel = viewModel.get('filterModel');
        // filterModel.getParent().getCache('parentViewModel') 查询区字段
        // filterModel.getParent().getParent().getCache('parentViewModel') 查询区设计态字段
        const referViewModel = filterModel?.getRootParent?.()?.getCache('parentViewModel');
        //  如果是参照内查询区就是referViewModel 如果是页面上查询区就是单据模型
        viewModel = referViewModel?.getParams?.().referModel?.getRootParent() || referViewModel;
      }
      return viewModel;
    };
  
    // 独立集成mdf 的过滤区
    baseModel.prototype.isMdfFilter = function () {
      let viewModel = this.getCache('parentViewModel');
      while (viewModel) {
        if (viewModel.getCache('mdfVM')) return true;
        viewModel = viewModel.getCache('parentViewModel');
      }
      return false;
    }
  
    baseModel.prototype.setName = function (name) {
      this.setCache('name', name);
    };
  
    baseModel.prototype.getName = function () {
      return this.getCache('name');
    };
  
    baseModel.prototype.getDomainKey = function () {
      return this._get_data('domainKey');
    }
  
    baseModel.prototype.setDomainKey = function (domainKey) {
      this._set_data('domainKey', domainKey);
    }
  
    baseModel.prototype.setServiceCode = function (serviceCode) {
      this._set_data('serviceCode', serviceCode);
    }
  
    baseModel.prototype.getServiceCode = function () {
      return this._get_data('serviceCode');
    }
  
    // 别名：与 getServiceCode 等价（billno 取 serviceCode）
    baseModel.prototype.getDiworkCode = function () {
      return this.getServiceCode();
    }
  
    /** 检查特性开关是否开启——委托全局 ttx.feature，作用域为当前 model 实例（沿用 this.features） */
    baseModel.prototype.hasFeature = function (feature) {
        return ttx.feature.isEnabled(feature, this);
    };

    baseModel.prototype.enableFeature = function (feature) {
      ttx.feature.enable(feature, this);
    }
  
    baseModel.prototype.disableFeature = function (feature) {
      ttx.feature.disable(feature, this);
    }
  
    baseModel.prototype.addListener = function (listener) {
      const listeners = this._get_data('listeners');
      if (listeners.indexOf(listener) >= 0) return;
      listeners.push(listener);
      this.updateListenerState(listener);
    };
  
    baseModel.prototype.removeListener = function (listener) {
      const listeners = this._get_data('listeners');
      let index;
      if ((index = listeners.indexOf(listener)) < 0) return;
      listeners.splice(index, 1);
    };
  
    baseModel.prototype.get = function (name) {
      return this._get_data(name);
    };
  
    baseModel.prototype.setDirty = function (dirty) {
      if (dirty) {
        this._set_data('isDirty', true);
        return;
      }
      this._del_data('isDirty');
      this._set_data('originalData', this.getData());
    };
  
    baseModel.prototype.backupState = function () {
      this._set_data('originalState', {
        bCanModify: this._get_data('bCanModify'),
        bShowIt: this._get_data('bShowIt'),
        visible: this._get_data('visible'),
        bIsNull: this._get_data('bIsNull'),
      });
    }
  
    baseModel.prototype.restoreState = function () {
      const originalState = this._get_data('originalState');
      if (!originalState) return;
      for (const attr in originalState) {
        if (attr === 'visible') {
          const hide = this.get('visible') !== originalState[attr];
          hide && this.setVisible(originalState[attr] !== false);
        } else if (attr === 'bCanModify' && originalState[attr] !== undefined && this.get('bCanModify') !== undefined && this.get('bCanModify') !== originalState[attr]) {
          this?.doPropertyChange('setState', { bCanModify: originalState[attr] })
        } else if (this.get('bIsNull') === false && this.get('bIsNull') !== originalState[attr] && attr === 'bIsNull') {
          // 审批流只设置bIsNull=false
          this?.doPropertyChange('setState', { bIsNull: originalState[attr] })
        }
        this._set_data(attr, originalState[attr]);
      }
    }
  
    // 获取当前字段/表单的数据值（_data.value）
    // 对于字段级 SimpleModel：返回单个字段值（如 "张三"）
    // 对于根 VM（表单页）：返回整个表单数据对象（如 { name: "张三", price: 100 }）
    baseModel.prototype.getData = function () {
      return this._get_data('value');
    };
  
    baseModel.prototype.getText = function (showEmpty) {
      if (this._get_data('bHidden') || !this._get_data('bShowIt')) return;
      const title = this._get_data('cShowCaption');
      const text = this.getData();
      if (!title || !showEmpty && ttx.utils.isEmpty(text)) return;
      return { title, text };
    }
  
    baseModel.prototype.setState = function (name, value, ctrlName) {
      if (!ctrlName)
        this._set_data(name, value, true);
      const state = {};
      state[name] = value;
      this.doPropertyChange('setState', state, ctrlName);
    };
  
    baseModel.prototype.getState = function (name, ctrlName) {
      if (!ctrlName)
        return this._get_data(name);
      const listeners = this._get_data('listeners');
      const listener = listeners.find(function (item) {
        return item.ctrlName === ctrlName;
      });
      if (!listener)
        return this._get_data(name);
      return listener.getState(name);
    };
  
    baseModel.prototype.fireEvent = function (eventName, args) {
      // before 事件返回 false 时阻止操作
      if (!this.execute('before' + eventName, args)) return false;
      // 执行主事件
      this.execute(eventName, args);
      // 执行 after 事件
      this.execute('after' + eventName, args);
      return true;
    };
  
    baseModel.prototype.setRequestMask = function (mask) {
      this.requestMask = mask;
      this.getGridModel?.()?.setRequestMask(mask)
      this.getTreeModel?.()?.setRequestMask(mask)
    };
  
    baseModel.prototype.getProxy = function () {
      return this._get_data('proxy');
    };
  
    baseModel.prototype.notifyListener = function (listener, name, value) {
      if (typeof listener[name] !== 'function')
        return false;
      this.viewUpdater(listener, name, value);
      return true;
    };
  
    baseModel.prototype.viewUpdater = function (listener, name, value) {
      let propertyName;
      if (name === 'setState') {
        listener[name](value);
      } else {
        propertyName = this.getName();
        listener[name](value, propertyName);
      }
    }
  
    /** 通知所有监听器属性变化（参照 cb 模式） */
    baseModel.prototype.doPropertyChange = function (name, value, ctrlName) {
      return ttx.utils.reactBatchUpdate(function () {
        var listeners = this._get_data('listeners');
        if (!listeners.length) return listeners;
        var notNotifiedListeners = [];
        if (!ctrlName) {
          listeners.forEach(function (item) {
            if (!this.notifyListener(item, name, value))
              notNotifiedListeners.push(item);
          }, this);
        } else {
          var listener = listeners.find(function (item) {
            return item.ctrlName === ctrlName;
          });
          if (listener) {
            if (!this.notifyListener(listener, name, value))
              notNotifiedListeners.push(listener);
          }
        }
        if (notNotifiedListeners.length) return notNotifiedListeners;
      }.bind(this), true);
    };

    baseModel.prototype.setReadOnly = function (value, ctrlName) {
      // if (this.getState('bCanModify') === false)
      //   value = true;
      if (this.getState('bNotModify') === false)
        value = false;
      this.setState('readOnly', value, ctrlName);
    };
  
    baseModel.prototype.getReadOnly = function (ctrlName) {
      return this.getState('readOnly', ctrlName);
    };
  
    baseModel.prototype.setEnable = function (value) {
      this.setState('enable', value);
    };
  
    baseModel.prototype.setDisabled = function (value, ctrlName) {
      if (this._get_data('bCanModify') === false)
        value = true;
      if (!ctrlName)
        this._set_data('disabled', value, true);
      const notNotifiedListeners = this.doPropertyChange('setDisabled', value, ctrlName);
      if (notNotifiedListeners) {
        const state = { disabled: value };
        notNotifiedListeners.forEach(function (listener) {
          this.notifyListener(listener, 'setState', state);
        }, this);
      }
    };
  
    baseModel.prototype.getDisabled = function (ctrlName) {
      return this.getState('disabled', ctrlName);
    };
  
    /** 运行时设置必填：审批流字段权限会按环节覆盖模板里的必填配置 */
    baseModel.prototype.setRequired = function (value, ctrlName) {
      if (!ctrlName)
        this._set_data('required', value, true);
      const notNotifiedListeners = this.doPropertyChange('setRequired', value, ctrlName);
      if (notNotifiedListeners) {
        const state = { required: value };
        notNotifiedListeners.forEach(function (listener) {
          this.notifyListener(listener, 'setState', state);
        }, this);
      }
    };
  
    baseModel.prototype.setVisible = function (value, ctrlName) {
      // 降低影响范围 适配钉耙组件，这里暂时不使用getBooleanValue 避免范围过大误杀
      if (value === 0) {
        value = false;
      }
      if (value === 1) {
        value = true;
      }
      if (!ctrlName)
        this._set_data('visible', value, true);
      const notNotifiedListeners = this.doPropertyChange('setVisible', value, ctrlName);
      if (notNotifiedListeners) {
        const state = { visible: value };
        notNotifiedListeners.forEach(function (listener) {
          this.notifyListener(listener, 'setState', state);
        }, this);
      }
    };
  
    baseModel.prototype.getVisible = function (ctrlName) {
      return this.getState('visible', ctrlName);
    };
  
    baseModel.prototype.setChecking = function (checking) {
      this._set_data('checking', checking);
    };
  
    baseModel.prototype.setValidateMsg = function (val) {
      const { type = 'error', msg } = val;
      this.doPropertyChange('validate', {
        type: type,
        message: msg
      });
    };
  
    baseModel.prototype.setCheckMsg = function (msg) {
      this._set_data('checkMsg', msg);
      const type = msg ? 'error' : '';
      this.doPropertyChange('validate', {
        type: type,
        message: msg
      });
    };
  
    baseModel.prototype.setFocus = function (value) {
      this.doPropertyChange('focus');
      this._set_data('focus', value, true);
    };
  
    baseModel.prototype.onFirst = function (name, callback, context) {
      if (!name || !callback) return;
      const events = this._get_data('firstevents') || {};
      const event = events[name] || (events[name] = []);
      event.splice(0, 0, { callback: callback, context: context });
    }
  
    // 注册事件监听（扩展脚本/插件通过 vm.on('afterValueChange', callback) 注册回调）
    // loadLazyActions=true 时回放已排队的延迟事件（用于扩展脚本 on 时补收已触发的事件）
    baseModel.prototype.on = function (name, callback, context, loadLazyActions) {
      if (!name || !callback) return;
      const events = this._get_data('events');
      const event = events[name] || (events[name] = []);
      event.push({ callback: callback, context: context });
  
      if (loadLazyActions) {
        // 如果loadLazyActions为true，说明指明了要执行扩展脚本中的execute（如果有）。此时做一些处理
        const lazyActions = this._get_data('lazyActions') || {}
        if (lazyActions[name]?.length) {
          // 按照顺序遍历lazyActions中名称相同的事件
          for (const args of lazyActions[name]) {
            if (ttx.rest.nodeEnv === 'development') {
              callback.apply(context || this, args);
            } else {
              try {
                callback.apply(context || this, args);
              } catch (e) {
                ttx.console.error(Error.E03000, '[baseModel.prototype.on]: 事件执行错误, execute[' + name + '] exception: ', e.stack);// @notranslate
              }
            }
          }
        }
      }
    };
  
    function unEvents (name, callback, events) {
      if (!name || !events || !events[name]) return;
      if (!callback) {
        delete events[name];
      } else {
        const index = events[name].findIndex(function (value) {
          if (value.callback === callback)
            return true;
        });
        if (index !== -1)
          events[name].splice(index, 1);
      }
    }
  
    baseModel.prototype.un = function (name, callback) {
      const events = this._get_data('events');
      unEvents(name, callback, events)
      const firstevents = this._get_data('firstevents');
      unEvents(name, callback, firstevents)
    };
  
    baseModel.prototype.hasEvent = function (name) {
      if (!name) return;
      const events = this._get_data('events');
      let length = events?.[name]?.length || 0;
      const firstevents = this._get_data('firstevents');
      length += (firstevents?.[name]?.length || 0)
      return length;
    };
  
    // 触发事件：遍历 events[name] 和 firstevents[name] 数组，逐个调用回调
    // 回调返回 false 可阻止后续回调执行（中断链）
    // lazyExecuteMode 时先入队再执行（用于扩展脚本延迟送达）
    baseModel.prototype.execute = function (name, ...args) {
      if (baseModel.prototype.lazyExecuteMode) {
        return this.lazyExecute(name, ...args)
      }
      return this._execute(name, ...args)
    }
  
    baseModel.prototype._asyncExecute = function (events, ...args) {
      let result = true;
      const allEvents = events || [];
      let promiseResult = false;
      for (let i = 0; i < allEvents.length; i++) {
        const event = allEvents[i];
        if (result === false) return;
        if (promiseResult) return;
        let returnData;
        if (ttx.rest.nodeEnv === 'development') {
          returnData = event.callback.apply(event.context || this, args);
        } else {
          try {
            returnData = event.callback.apply(event.context || this, args);
          } catch (e) {
            if (!e.isAddedMsg) {
              e.message = '(领域扩展事件执行失败，请打开控制台查看详细错误信息并修复):' + e.message; // @notranslate
            }
            e.isAddedMsg = true;
            throw e;
          }
        }
        if (returnData instanceof ttx.promise) {
          promiseResult = true;
          result = returnData;
          result.callbacks && result.callbacks.push({
            reject: () => result.reject(),
            resolve: () => this._asyncExecute(allEvents.slice(i + 1), ...args),
          })
        } else {
          result = returnData === false ? false : result
        }
      }
      return result;
    }
  
    baseModel.prototype._execute = function (name, ...args) {
      return ttx.utils.reactBatchUpdate(() => {
        if (!name) return;
        const events = this._get_data('events')[name];
        const firstevents = this._get_data('firstevents')?.[name];
        if (!events && !firstevents) return true;
        if (window.__ASYNC_EXECUTE__) {
          const allEvents = [...(firstevents || []), ...(events || [])]
          const result = this._asyncExecute(allEvents, ...args);
          return result;
        } else {
          let result = true;
          [...(firstevents || []), ...(events || [])].forEach(function (event) {
            if (result === false) return;
            let returnData;
            if (ttx.rest.nodeEnv === 'development') {
              returnData = event.callback.apply(event.context || this, args);
            } else {
              try {
                returnData = event.callback.apply(event.context || this, args);
              } catch (e) {
                if (window.__THROW_REAL_ERROR_LOG__) {
                  if (!e.isAddedMsg) {
                    e.message = '(领域扩展事件执行失败，请打开控制台查看详细错误信息并修复):' + e.message; // @notranslate
                  }
                  e.isAddedMsg = true;
                  throw e;
                } else {
                  ttx.console.error(Error.E03000, '[baseModel.prototype._execute]: execute[' + name + '](领域扩展事件执行失败，请打开控制台查看详细错误信息并修复):', e.stack); // @notranslate
                }
              }
            }
            result = returnData instanceof ttx.promise ? returnData : (returnData === false ? false : result);
          }, this);
          return result;
        }
      }, !window.__reactBatchUpdateFalse)
    };
  
    baseModel.prototype.lazyExecute = function (name, ...args) {
      // 本函数用于在扩展脚本中临时代替execute函数。作用是使得扩展脚本中的事件能够准确被送达。
      // 原理是先将扩展脚本中的事件保存到 lazyActions，并在组件调用on方法绑定事件的时候，根据 loadLazyActions 参数决定是否立刻执行一次（或多次）事件
      if (!name) return;
      const lazyActions = this._get_data('lazyActions') || {}
      lazyActions[name] = lazyActions[name] || []
      lazyActions[name].push(args)
      this._set_data('lazyActions', lazyActions)
      return this._execute(name, ...args)
    };
  
    baseModel.prototype.promiseExecute = function (check, eventName) {
      let name = check;
      let sliceStart = 1;
      if (typeof check === 'boolean') {
        name = eventName;
        sliceStart = 2;
      }
      if (!name) return;
      const args = Array.prototype.slice.call(arguments, sliceStart);
      if (!args.length) return;
      const callbackIndex = args.length - 1;
      let callback = args[callbackIndex];
      let errCallBack;
      if (typeof callback !== 'function') {
        if (typeof callback === 'object' && callback[0] && typeof callback[0] === 'function' && callback[1] && typeof callback[1] === 'function') {
          errCallBack = callback[1];
          callback = callback[0];
        } else return;
      }
      args.splice(callbackIndex, 1);
      args.unshift(name);
      const returnData = this.execute.apply(this, args);
      if (returnData instanceof ttx.promise) {
        const self = this;
        returnData.then(function () {
          callback.apply(self, arguments);
        }, function () {
          if (errCallBack && typeof errCallBack === 'function')
            errCallBack();
        });
      } else {
        if (!returnData) {
          if (errCallBack && typeof errCallBack === 'function')
            errCallBack();
          return;
        }
        ttx.utils.reactBatchUpdate(() => {
          callback.call(this);
        }, true)
      }
    };
    // 设置临时缓存（不触发监听器更新，用于内部状态如 parent/isLoading 等）
    baseModel.prototype.setCache = function (key, value) {
      this._get_data('cache')[key] = value;
    };

    // 读取临时缓存
    baseModel.prototype.getCache = function (key) {
      return this._get_data('cache')?.[key];
    };
  
    baseModel.prototype.clearCache = function (key) {
      delete this._get_data('cache')[key];
    };
  
    baseModel.prototype.runGC = function () {
      this._cls_data();
    };
  
    baseModel.prototype.setEditPrevvalue = function(value) {
      if (this.getCache('isValidate')) {
        return;
      }
      if (this?.getRootParent()?.getCache('isLoadData')) {
        return;
      }
      if (this.getRootParent?.()?.getParams?.()?.firstLoadData === true) { // 扩展脚本必须阻止第一次加载数据导致变更
        this.getRootParent().getParams().firstLoadData = false
        return;
      }
      const model = this?.getParent?.()?.getParent?.()
      if (model?.getCache('isSetFocus') && !(this instanceof ttx.models.ReferModel)) {
        return
      }
      if (this.getCache('isClearCall') && model) {
        // 不处理
      } else {
        if (model instanceof ttx.models.GridModel) {
        }
        else {
          if (ttx.utils.isEmpty(this._get_data('prevvalue'))) {
            const changeArr = this.getRootParent?.()?.getCache('changeArr') || []
            if (ttx.utils.isArray(value)) {
              value = value.join()
            } else if (typeof value !== 'string' && value !== null) {
              value = JSON.stringify(value)
            }
            value = !ttx.utils.isEmpty(value) ? value : '--'
            this._set_data('prevvalue', value)
            this.doPropertyChange('setState', {prevvalue: value})
            changeArr.push(this)
            this.getRootParent?.()?.setCache('changeArr', changeArr)
          }
        }
      }
    }
  
    baseModel.prototype.clearEditPrevvalue = function (params) {
      const {viewmodel} = params
      const changeArr = viewmodel.getCache('changeArr')
      if (changeArr) {
        for (let i = 0; i < changeArr.length; i++) {
          const model = changeArr[i]
          model._del_data('prevvalue')
          model.doPropertyChange('setState', {prevvalue: undefined})
        }
      }
    }
  
    baseModel.prototype.setEditCellPrevvalue = function (params) {
      const {cellName, model, values, args} = params
      if (this.getRootParent?.()?.getParams?.()?.firstLoadData === true) { // 扩展脚本必须阻止第一次加载数据导致变更
        this.getRootParent().getParams().firstLoadData = false
        return;
      }
      if (this.getCache('isValidate')) return
      const index = model?.getFocusedRowIndex()
      if (model?.getCache('isSetFocus') && !(this instanceof ttx.models.ReferModel)) {
        return
      }
      const dataSource = model?._get_data('dataSource')
      if (this.getCache('isClearCall') && model) {
        // 不处理
      } else {
        // 初始化所有历史状态
        model?._set_data('isHistoryChange', true)
        const rowsDataState = model?._get_data('rowsDataState');
        for (let i = 0; i < rowsDataState.length; i++) {
          if (rowsDataState[i] === ttx.models.DataStates.Insert) {
            dataSource[i]._historyStatus = ''
          } else if (ttx.utils.isEmpty(dataSource[i]._historyStatus)) {
            dataSource[i]._historyStatus = 'normal'
          }
        }
        if (cellName || values || args) {
          if (rowsDataState[index] === ttx.models.DataStates.Insert || rowsDataState[index] === ttx.models.DataStates.Delete) { return }
          const cellModel = model.getEditRowModel().get(cellName)
          const _cControlType = cellModel?._get_data('cControlType')?.toLocaleLowerCase()
          if (_cControlType === 'lineno') { return }
          if (values) {
            for (let i = 0; i < values.length; i++) {
              // 兼容批改
              if (dataSource.length > 0 && ttx.utils.isEmpty(dataSource[values[i].rowIndex][`_prev${values[i].cellName}`])) {
                if (rowsDataState[values[i].rowIndex] === ttx.models.DataStates.Insert || rowsDataState[values[i].rowIndex] === ttx.models.DataStates.Delete) continue;
                dataSource[values[i].rowIndex][`_prev${values[i].cellName}`] = dataSource[values[i].rowIndex][values[i].cellName]
                dataSource[values[i].rowIndex]._historyStatus = 'update'
              }
            }
          } else if (args) {
            const referValues = args.value?.items
            for (let i = 0; i < referValues.length; i++) {
              if (dataSource.length > 0 && ttx.utils.isEmpty(dataSource[referValues[i].index][`_prev${cellName}`])) {
                if (rowsDataState[referValues[i].index] === ttx.models.DataStates.Insert || rowsDataState[referValues[i].index] === ttx.models.DataStates.Delete) continue;
                dataSource[referValues[i].index][`_prev${cellName}`] = dataSource[referValues[i].index][cellName]
                dataSource[referValues[i].index]._historyStatus = 'update'
              }
            }
          } else if (dataSource.length > 0 && ttx.utils.isEmpty(dataSource[index]?.[`_prev${cellName}`]) && index !== -1) { // 这里-1是因为批改的时候focusRowIndex都是-1
            if (cellName === 'taxRate' || cellName === 'oriTax') {
              dataSource[index][`_prev${cellName}`] = dataSource[index][cellName] || '0'
            } else {
              dataSource[index][`_prev${cellName}`] = dataSource[index][cellName] || '--'
            }
            dataSource[index]._historyStatus = 'update'
            if (_cControlType === 'datepicker') {
              const listeners = model._get_data('listeners')
              for (let i = 0; i < listeners.length; i++) {
                if (listeners[i].dataList) {
                  listeners[i].dataList[index][`_prev${cellName}`] = dataSource[index][cellName] || '--'
                }
              }
            }
            model._set_data('prevvalue', dataSource)
            cellModel.doPropertyChange('setState', { prevvalue: dataSource[index][`_prev${cellName}`] });
          }
        }
      }
    }
  
    /**
     * 设置 ViewModel 的请求代理
     * 对应 MDF viewmodel.setProxy
     *
     * proxyConfig: { key: { url, method, options: { mask, uniform, async, deflate, timeout,
     *   contentType, domainKey, dateType, withCredentials, jsonp, serviceCode, query } } }
     *
     * 返回 proxy 对象：
     *   - proxy.key(params, callback)  异步调用，callback(err, result)
     *   - proxy.key(params)            同步调用（options.async=false），直接返回结果
     *
     * 框架自动处理 domainKey：若 proxyConfig 中未指定 options.domainKey，
     * 则使用当前 ViewModel 的 domainKey 注入。
     *
     * @param {object} proxyConfig 代理配置
     * @returns {object} proxy 对象
     */
    baseModel.prototype.setProxy = function (proxyConfig) {
      if (!proxyConfig || typeof proxyConfig !== 'object') return this.getProxy();
      // 框架自动处理 domainKey：未指定时注入当前 VM 的 domainKey
      var vmDomainKey = this.getDomainKey && this.getDomainKey();
      if (vmDomainKey) {
        for (var key in proxyConfig) {
          var cfg = proxyConfig[key];
          if (!cfg) continue;
          cfg.options = cfg.options || {};
          if (cfg.options.domainKey === undefined) {
            cfg.options.domainKey = vmDomainKey;
          }
        }
      }
      var proxy = ttx.rest.DynamicProxy.create(proxyConfig);
      this._set_data('proxy', proxy);
      return proxy;
    };

    return baseModel;
  });
  